import { css } from '@emotion/react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';
import debounce from 'lodash/debounce';
import t from 'react-translate';
import { useAppDispatch } from 'redux/store';
import { wrapThunkAction } from 'redux/utils';
import { usePaginatedApi } from 'shared/hooks/use-paginated-api';
import useInfiniteScroll from 'shared/hooks/use-infinite-scroll';
import NvTable, { Column } from 'shared/components/nv-table';
import NvCheckbox from 'shared/components/inputs/nv-checkbox';
import NvNoResults from 'shared/components/nv-no-results-panel';
import MentoringProgramContext, { MentoringProgramHomeUIContext } from 'athena/components/mentoring-program/context';
import AthenaTextInput, { AthenaTextInputType } from 'athena/components/text-input';
import PillButton from 'athena/components/pill-button';
import LoadingRow from 'athena/components/mentoring-program/loading-row';
import { bulkEnrollMentoringProgramRecruitmentApplications, getMentoringProgramRecruitmentApplications } from 'redux/actions/mentoring-program-recruitments';
import { RecruitmentApplication } from 'redux/schemas/models/mentoring-program-recruitments';
import { getMentoringProgramRecruitmentApplicationsList } from 'redux/selectors/mentorship-program-recruitments';
import { getMentoringProgramAliases } from 'redux/selectors/mentoring-programs';
import { threeQuartersSpacing } from 'styles/global_defaults/scaffolding';
import { hexToRgbaString } from 'styles/global_defaults/colors';
import { darkGray, alto } from 'athena/styles/colors';
import { ReactComponent as LightGrayAvatar } from 'styles/icons/light-gray-avatar.svg';
import { config } from '@config/pendo.config.json';
import { ColumnID } from './recruitment-row/types';
import RecruitmentRow from './recruitment-row';
import { selectedCheckboxStyles } from './recruitment-row/checkbox-cell';
import { PAGE_SIZE } from '../constants';

type RecruitmentRole = 'mentor' | 'mentee';

const ROW_HEIGHT = 70;


type RecruitmentProps = {
  scrollEnabled: boolean;
  onScroll: React.ComponentProps<'div'>['onScroll'];
};

const Recruitment = ({
  scrollEnabled,
  onScroll,
}: RecruitmentProps) => {
  const styles = css`
    .tabs-container {
      gap: ${threeQuartersSpacing}px;
    }

    .bulk-edit-row {
      height: 70px;
    }

    .table-body {
      overflow-y: ${scrollEnabled ? 'auto' : 'hidden'};

      .empty-ui {
        height: 250px;
        color: ${darkGray};
        background-color: ${hexToRgbaString(alto, 0.2)};
      }
    }
  `;

  const dispatch = useAppDispatch();
  const scrollRef = useRef<HTMLDivElement>();
  const mountedRef = useRef(false);
  // Detect when the user scrolls within 3 rows of the bottom
  const reachedEnd = useInfiniteScroll(scrollRef.current, ROW_HEIGHT * 3);

  const { countState, mentoringProgram } = useContext(MentoringProgramContext);
  const { id: programId, recruitments } = mentoringProgram || {};
  const { withOverflowFlexGrowClass } = useContext(MentoringProgramHomeUIContext);

  const mentoringProgramAliases = useSelector((state) => getMentoringProgramAliases(state, programId));

  const bodyProps = {
    onScroll,
    ref: scrollRef,
    className: `table-body ${withOverflowFlexGrowClass}`,
  };

  const [roleFilter, setRoleFilter] = useState<RecruitmentRole>('mentor');
  const [selectedApplications, setSelectedApplications] = useState<any[]>([]);
  const [sorting, setSorting] = useState([{
    id: ColumnID.DATE_SUBMITTED,
    desc: false,
  }]);
  const [textSearch, setTextSearch] = useState('');
  const debounceSetTextSearch = useMemo(
    () => debounce(setTextSearch, 500),
    [],
  );

  const activeRecruitmentId = roleFilter === 'mentor'
    ? recruitments?.mentor?.id
    : recruitments?.mentee?.id;

  const recruitmentCount = roleFilter === 'mentor'
    ? countState.result?.recruitmentApplicationsCount?.mentor
    : countState.result?.recruitmentApplicationsCount?.mentee;

  const handleRoleFilterChange = (role: RecruitmentRole) => {
    setRoleFilter(role);
    setSelectedApplications([]);
    setTextSearch('');
  };

  const tabs = useMemo(() => [
    {
      role: 'mentor' as RecruitmentRole,
      label: t.MENTORING_PROGRAMS.ROLES.PARTICIPANTS_CATEGORY.NUM_MENTORS({
        MentorsAlias: mentoringProgramAliases.MentorsAlias,
        mentorsCount: countState.result?.recruitmentApplicationsCount?.mentor ?? 0,
      }),
      dataQa: config.pendo.athena.mentorshipProgram.recruitment.filterByRole.mentor,
    },
    {
      role: 'mentee' as RecruitmentRole,
      label: t.MENTORING_PROGRAMS.ROLES.PARTICIPANTS_CATEGORY.NUM_MENTEES({
        MenteesAlias: mentoringProgramAliases.MenteesAlias,
        menteesCount: countState.result?.recruitmentApplicationsCount?.mentee ?? 0,
      }),
      dataQa: config.pendo.athena.mentorshipProgram.recruitment.filterByRole.mentee,
    },
  ], [mentoringProgramAliases, countState.result]);

  const params = useMemo(() => {
    const sortingObject: any = {};
    const currentSorting = sorting[0];

    if (currentSorting) {
      sortingObject.sortBy = currentSorting.id;
      sortingObject.sortDesc = currentSorting.desc;
    }

    return {
      pageSize: PAGE_SIZE,
      textSearch: textSearch?.trim() || undefined,
      ...sortingObject,
      // Include roleFilter to trigger re-fetch when the active tab changes
      roleFilter,
    };
  }, [sorting, textSearch, roleFilter]);

  const {
    reset,
    result,
    loadMore,
    isLoading,
    page,
  } = usePaginatedApi(
    (p) => dispatch(
      getMentoringProgramRecruitmentApplications({
        programId,
        recruitmentId: activeRecruitmentId,
        ...p,
      }),
    ).then(actions => actions.payload),
    params,
    getMentoringProgramRecruitmentApplicationsList,
  );

  useEffect(() => {
    if (reachedEnd) {
      loadMore();
    }
  }, [loadMore, reachedEnd]);

  // The applications list re-fetches whenever params change (role tab toggle,
  // search, sort), so refresh the mentor/mentee counts together with it to
  // keep them in sync. The first render is skipped because counts have just
  // been fetched on mount by useProgramSummaryCounts.
  useEffect(() => {
    if (mountedRef.current) {
      countState.refresh();
    } else {
      mountedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const getData = () => {
    if (isLoading && page === 1) return [];
    return result ?? [];
  };

  const isApplicationSelected = (applicationId: number) => selectedApplications.some(({ id }) => id === applicationId);

  const loadedApplications = result ?? [];
  const allSelected = loadedApplications.length > 0
    && loadedApplications.every((application) => isApplicationSelected(application.id));
  const someSelected = loadedApplications.some((application) => isApplicationSelected(application.id));

  const applicationTableColumns: Column<RecruitmentApplication>[] = useMemo(() => [
    {
      id: ColumnID.CHECKBOX,
      headerContent: (
        <NvCheckbox
          css={selectedCheckboxStyles}
          name='select-all'
          label=''
          ariaLabel={t.MENTORING_PROGRAMS.RECRUITMENT.ARIA_LABEL.SELECT_ALL()}
          checked={allSelected}
          indeterminate={someSelected && !allSelected}
          onChange={() => setSelectedApplications(allSelected ? [] : (result ?? []))}
        />
      ),
      accessorFn: (application) => application,
      gridTemplateColumn: '40px',
    },
    {
      id: ColumnID.NAME,
      enableSorting: true,
      header: t.MENTORING_PROGRAMS.RECRUITMENT.COLUMNS.NAME(),
      accessorFn: (application) => application.user,
      gridTemplateColumn: '33%',
    },
    {
      id: ColumnID.DATE_SUBMITTED,
      enableSorting: true,
      header: t.MENTORING_PROGRAMS.RECRUITMENT.COLUMNS.DATE_SUBMITTED(),
      accessorFn: (application) => application.createdAt,
      gridTemplateColumn: '1fr',
    },
    {
      id: ColumnID.ADD_BUTTON,
      accessorFn: (application) => application,
    },
  ], [allSelected, someSelected, result]);

  const refreshAfterEnroll = () => {
    setSelectedApplications([]);
    reset();
    countState.refresh();
  };

  const handleBulkAdd = () => {
    wrapThunkAction(
      dispatch(bulkEnrollMentoringProgramRecruitmentApplications({
        programId,
        recruitmentId: activeRecruitmentId,
        applicationIds: [...selectedApplications.map(({ id }) => id)],
        role: roleFilter,
        ...mentoringProgramAliases,
      })),
    ).then(refreshAfterEnroll);
  };

  const getBulkAddLabel = () => {
    const isSingleSelection = selectedApplications.length === 1;
    if (roleFilter === 'mentor') {
      return isSingleSelection
        ? t.MENTORING_PROGRAMS.RECRUITMENT.ADD_AS_MENTOR(mentoringProgramAliases)
        : t.MENTORING_PROGRAMS.RECRUITMENT.ADD_AS_MENTORS(mentoringProgramAliases);
    }
    return isSingleSelection
      ? t.MENTORING_PROGRAMS.RECRUITMENT.ADD_AS_MENTEE(mentoringProgramAliases)
      : t.MENTORING_PROGRAMS.RECRUITMENT.ADD_AS_MENTEES(mentoringProgramAliases);
  };

  return (
    <div css={styles} className={`d-flex flex-column ${withOverflowFlexGrowClass}`}>
      {countState.result && (
        <div className='tabs-container d-flex flex-row flex-wrap mb-4'>
          {tabs.map(({ role, label, dataQa }) => (
            <PillButton
              key={role}
              text={label}
              selected={role === roleFilter}
              onClick={() => handleRoleFilterChange(role)}
              dataQa={dataQa}
            />
          ))}
        </div>
      )}
      <div className='d-flex mb-4'>
        <AthenaTextInput
          icon='search'
          type={AthenaTextInputType.SEARCH}
          value={textSearch}
          showLabel={false}
          className='w-100'
          onChange={({ target: { value } }) => debounceSetTextSearch(value)}
          placeholder={t.MENTORING_PROGRAMS.RECRUITMENT.SEARCH_PLACEHOLDER()}
          dataQa={config.pendo.athena.mentorshipProgram.recruitment.searchRecruitmentInputField}
        />
      </div>
      <div className='bulk-edit-row d-flex align-items-center justify-content-between px-4 bg-color-gray-7 border-radius-2'>
        <div className='text-small color-gray-1 semi-bold'>
          {selectedApplications.length === 0 && roleFilter === 'mentor' && t.MENTORING_PROGRAMS.RECRUITMENT.SELECT_CANDIDATES(mentoringProgramAliases)}
          {selectedApplications.length === 0 && roleFilter === 'mentee' && t.MENTORING_PROGRAMS.RECRUITMENT.SELECT_MENTEE_CANDIDATES(mentoringProgramAliases)}
          {selectedApplications.length > 0 && t.MENTORING_PROGRAMS.RECRUITMENT.M_OUT_OF_N_SELECTED(selectedApplications.length, recruitmentCount)}
        </div>
        <Button
          variant='primary'
          disabled={!selectedApplications.length}
          onClick={handleBulkAdd}
          data-qa={config.pendo.athena.mentorshipProgram.recruitment.bulkAddUsersButton}
        >
          {getBulkAddLabel()}
        </Button>
      </div>
      <NvTable<RecruitmentApplication>
        data={getData()}
        onSortingChange={setSorting}
        columns={applicationTableColumns}
        className={`d-flex flex-column ${withOverflowFlexGrowClass}`}
        RowComponent={RecruitmentRow}
        bodyProps={bodyProps}
        state={{
          sorting,
        }}
        meta={{
          isApplicationSelected,
          setSelectedApplications,
          roleFilter: () => roleFilter,
          activeRecruitmentId: () => activeRecruitmentId,
          refreshAfterEnroll,
        }}
        renderAfterRows={(afterRowsProps) => {
          if (result != null && !result.length) {
            return textSearch ? (
              <NvNoResults
                hideClearSearch={false}
                action={() => setTextSearch('')}
                clearText={t.SEARCH.CLEAR()}
                noResultsText={t.SEARCH.NO_RESULTS_FOUND()}
              />
            ) : (
              <div className='d-flex flex-column align-items-center justify-content-center empty-ui'>
                <LightGrayAvatar width={80} height={80} className='mb-4' />
                <div className='text-medium gray-2'>
                  {roleFilter === 'mentor'
                    ? t.MENTORING_PROGRAMS.RECRUITMENT.NO_MENTORS(mentoringProgramAliases)
                    : t.MENTORING_PROGRAMS.RECRUITMENT.NO_MENTEES(mentoringProgramAliases)}
                </div>
              </div>
            );
          }

          return isLoading && <LoadingRow {...afterRowsProps} />;
        }}
      />
    </div>
  );
};

export default Recruitment;
